import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import config from "../../config";
import { Session, SessionStatus } from "../../models";
import { EmailTemplate } from "../../models/coachingNavigator/emailTemplate";
import { SessionService } from "../../services/coachingNavigator/sessionService";
import { EmailTemplateService } from "../../services/coachingNavigator/emailTemplateService";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import { MethodService } from "../../services/coachingNavigator/methodService";
import { dateTimeFormatter } from "../../util/dateFormatter";
import { useTranslation } from "react-i18next";
import SkeletonLoader from "../../components/skeletonLoader";
import { Context } from "../../components/context";
import EmailTemplateDialog, {
  EmailTemplateFormValues,
} from "../../components/coachingNavigator/emailTemplateDialog";

type Participant = {
  name: string;
  role: string;
  avatar?: string;
};

type MeetingInfoCardProps = {
  meetingName: string;
  dateTime: string;
  participants: Participant[];
};

type EmailTemplateSelectorProps = {
  selectedTemplate: string | null;
  templates: EmailTemplate[];
  onSelectTemplate: (templateId: string | null) => void;
  onCreateTemplate: () => void;
  creatingTemplate: boolean;
};

type DurationAdjusterProps = {
  scheduledDuration: number;
  actualDuration: number;
  onDurationChange: (duration: number) => void;
};

type CloseConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

const sessionService = new SessionService(config.api.baseUrl, "/sessions");
const emailTemplateService = new EmailTemplateService(
  config.api.baseUrl,
  "/emailTemplate",
);
const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");
const methodService = new MethodService(config.api.baseUrl, "/method");

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("");
}

function MeetingInfoCard({
  meetingName,
  dateTime,
  participants,
}: MeetingInfoCardProps) {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{ borderRadius: 3, backgroundColor: "white", p: 3, boxShadow: 1 }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("coachingNavigator.distributionScreen.meetingInformation")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 3,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 0.5
            }}>
            {t("coachingNavigator.distributionScreen.meetingName")}
          </Typography>
          <Typography variant="body1" sx={{
            fontWeight: 500
          }}>
            {meetingName}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 0.5
            }}>
            {t("coachingNavigator.distributionScreen.dateTime")}
          </Typography>
          <Typography variant="body1" sx={{
            fontWeight: 500
          }}>
            {dateTime}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 1.5
          }}>
          {t("coachingNavigator.distributionScreen.participants")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {participants.map((participant, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Avatar
                src={participant.avatar}
                alt={participant.name}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#DFE1E3",
                  color: "#242C63",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {getInitials(participant.name)}
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "text.primary"
                  }}>
                  {participant.name}
                </Typography>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {participant.role}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

function EmailTemplateSelector({
  selectedTemplate,
  onSelectTemplate,
  templates,
  onCreateTemplate,
  creatingTemplate,
}: EmailTemplateSelectorProps) {
  const { t } = useTranslation();

  if (templates.length === 0) {
    return (
      <Paper
        sx={{ borderRadius: 3, backgroundColor: "white", p: 3, boxShadow: 1 }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("coachingNavigator.emailTemplates.title")}
        </Typography>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("coachingNavigator.emailTemplates.emptyTitle")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}
          >
            {t("coachingNavigator.emailTemplates.emptySubtitle")}
          </Typography>
          <Button
            variant="contained"
            onClick={onCreateTemplate}
            disabled={creatingTemplate}
          >
            {t("coachingNavigator.emailTemplates.add")}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{ borderRadius: 3, backgroundColor: "white", p: 3, boxShadow: 1 }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("coachingNavigator.emailTemplates.title")}
      </Typography>
      <List disablePadding>
        {templates.map((template) => {
          const isSelected = selectedTemplate === template.id;
          return (
            <ListItemButton
              key={template.id}
              onClick={() => onSelectTemplate(isSelected ? null : template.id)}
              selected={isSelected}
              sx={[{
                borderRadius: 2,
                mb: 0.5,
                border: "1px solid",

                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "rgba(36, 44, 99, 0.04)",
                },

                "&.Mui-selected": {
                  backgroundColor: "rgba(36, 44, 99, 0.04)",
                  "&:hover": { backgroundColor: "rgba(36, 44, 99, 0.08)" },
                }
              }, isSelected ? {
                borderColor: "primary.main"
              } : {
                borderColor: "divider"
              }, isSelected ? {
                backgroundColor: "rgba(36, 44, 99, 0.04)"
              } : {
                backgroundColor: "transparent"
              }]}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Radio
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary"
                    }}>
                    {template.name}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      color: "text.secondary"
                    }}
                  >
                    {template.body || template.subject || ""}
                  </Typography>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
}

function DurationAdjuster({
  scheduledDuration,
  actualDuration,
  onDurationChange,
}: DurationAdjusterProps) {
  const minDuration = Math.max(5, scheduledDuration - 30);
  const maxDuration = scheduledDuration + 30;

  const [inputValue, setInputValue] = useState(actualDuration.toString());
  const { t } = useTranslation();

  useEffect(() => {
    setInputValue(actualDuration.toString());
  }, [actualDuration]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    onDurationChange(Math.round(val / 5) * 5);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (!Number.isNaN(parsed) && parsed >= 5) {
      onDurationChange(Math.round(parsed / 5) * 5);
    } else {
      setInputValue(actualDuration.toString());
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleInputBlur();
  };

  return (
    <Paper
      sx={{ borderRadius: 3, backgroundColor: "white", p: 3, boxShadow: 1 }}
    >
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t("coachingNavigator.distributionScreen.adjustDuration")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2">
            {minDuration} {t("coachingNavigator.minutes")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ htmlInput: {
                min: 5,
                style: {
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  width: 48,
                  padding: "6px 8px",
                },
              } }}
            />
            <Typography variant="body2">
              {t("coachingNavigator.minutes")}
            </Typography>
          </Box>
          <Typography variant="body2">
            {maxDuration} {t("coachingNavigator.minutes")}
          </Typography>
        </Box>

        <Box sx={{ px: 0.5 }}>
          <Slider
            value={Math.min(Math.max(actualDuration, minDuration), maxDuration)}
            min={minDuration}
            max={maxDuration}
            step={5}
            onChange={handleSliderChange}
          />
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
          >
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.distributionScreen.short")}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.distributionScreen.scheduledLabel")}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.distributionScreen.extended")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function CloseConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
}: CloseConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)} slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
    >
      <DialogTitle>
        {t("coachingNavigator.distributionScreen.closeDialogTitle")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("coachingNavigator.distributionScreen.closeDialogBody")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onOpenChange(false)} color="inherit">
          {t("button.cancel")}
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          {t("button.continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DistributionScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appContext = useContext(Context);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [scheduledDuration, setScheduledDuration] = useState<number>(60);
  const [actualDuration, setActualDuration] = useState<number>(60);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [coacheeEmail, setCoacheeEmail] = useState<string>("");
  const [confirming, setConfirming] = useState<boolean>(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [sessionResp, templateResp] = await Promise.all([
          sessionService.get(sessionId),
          emailTemplateService.getByCreator(
            (appContext.state.invoker?.email || "").trim(),
          ),
        ]);
        if (cancelled) return;
        setSession(sessionResp || null);
        setTemplates(Array.isArray(templateResp) ? templateResp : []);
        const durationMinutes = Number(sessionResp?.duration ?? 0) || 0;
        if (durationMinutes > 0) {
          setScheduledDuration(durationMinutes);
          setActualDuration(durationMinutes);
        }
        if (sessionResp?.clientId) {
          coacheeService
            .get(sessionResp.clientId)
            .then((c) => {
              if (!cancelled) setCoacheeEmail(c?.email || "");
            })
            .catch((err) => {
              console.error("Failed to load coachee email", err);
            });
        }
      } catch (err) {
        console.error("Failed to load distribution data", err);
        if (!cancelled)
          setError(t("coachingNavigator.distributionScreen.loadingError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const incrementCompletedSessions = useCallback(async (clientId: string) => {
    if (!clientId) return;

    try {
      const coachee = await coacheeService.get(clientId);
      const currentCompleted = Number.isFinite(coachee?.completedSessions)
        ? Math.max(Number(coachee.completedSessions), 0)
        : 0;
      const { _id: _ignoreId, id: _ignoreId2, __v: _ignoreV, ...coacheeData } = coachee as any;
      await coacheeService.save({
        ...coacheeData,
        id: clientId,
        completedSessions: currentCompleted + 1,
      });
    } catch (err) {
      console.error("Failed to increment coachee completed sessions", err);
    }
  }, []);

  const handleConfirm = async () => {
    if (!session?.id) return;
    setConfirming(true);
    try {
      const latestSession = await sessionService.get(session.id);
      const payload: Session = {
        ...latestSession,
        status: SessionStatus.Completed,
        duration: actualDuration,
      };
      const saved = await sessionService.save(payload);
      setSession(saved);
      if (saved?.clientId) {
        if (session?.status !== SessionStatus.Completed) {
          await incrementCompletedSessions(saved.clientId);
        }
        try {
          await coacheeService.recalculateCompletedHours(saved.clientId);
        } catch (recalcErr) {
          console.error("Failed to recalculate completed hours", recalcErr);
        }

        try {
          const coveredMethods = Array.isArray(saved?.methodsUsed)
            ? saved.methodsUsed.filter(Boolean)
            : [];
          if (coveredMethods.length > 0) {
            const creatorEmail = (appContext.state.invoker?.email || "").trim();
            const methods = creatorEmail
              ? await methodService.getByCreator(creatorEmail)
              : [];
            const nameById: Record<string, string> = {};
            (methods || []).forEach((m) => {
              const key = (m as { id?: string }).id;
              if (key) nameById[key] = (m as { name?: string }).name || key;
            });
            const coveredNames = coveredMethods.map((m) => nameById[m] || m);
            const coachee = await coacheeService.get(saved.clientId);
            const existing = Array.isArray(coachee?.methodsUsed)
              ? coachee.methodsUsed.filter(Boolean)
              : [];
            const merged = Array.from(new Set([...existing, ...coveredNames]));
            if (merged.length !== existing.length) {
              await coacheeService.save({ ...coachee, methodsUsed: merged });
            }
          }
        } catch (methodErr) {
          console.error("Failed to merge methods into coachee", methodErr);
        }
      }
      const targetId = saved?.id || session.id;
      navigate(`/sessions/${targetId}`);

      const template = templates.find((t) => t.id === selectedTemplate);
      if (template && coacheeEmail) {
        const subject = encodeURIComponent(template.subject || "");
        const body = encodeURIComponent(template.body || "");
        window.setTimeout(() => {
          window.location.href = `mailto:${encodeURIComponent(coacheeEmail)}?subject=${subject}&body=${body}`;
        }, 75);
      }
    } catch (err) {
      console.error("Failed to confirm session", err);
      setError(t("coachingNavigator.distributionScreen.confirmError"));
    } finally {
      setConfirming(false);
    }
  };

  const handleCloseConfirm = () => {
    setShowCloseDialog(false);
    console.log("Closing modal, keeping draft");
  };

  const handleCreateTemplate = useCallback(
    async (values: EmailTemplateFormValues) => {
      const creatorEmail = (appContext.state.invoker?.email || "").trim();
      const payload: EmailTemplate = {
        name: values.name,
        subject: values.subject,
        body: values.body,
        creator: creatorEmail || undefined,
      };

      try {
        setCreatingTemplate(true);
        const saved = await emailTemplateService.save(payload);
        setTemplates((prev) =>
          [...prev, saved].sort((a, b) =>
            (a.name || "").localeCompare(b.name || ""),
          ),
        );
        setSelectedTemplate(saved.id || null);
        setCreateTemplateOpen(false);
      } catch (err) {
        console.error("Failed to create email template", err);
      } finally {
        setCreatingTemplate(false);
      }
    },
    [appContext.state.invoker],
  );

  if (loading) {
    return (
      //TODO: replace with proper skeletons for each section instead of clientOverview variant
      <SkeletonLoader variant="clientOverview" />
    );
  }

  if (error || !session) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="body2" color="error">
          {error || t("coachingNavigator.sessionDetails.notFound")}
        </Typography>
      </Box>
    );
  }

  const start = session.startTime ? new Date(session.startTime) : null;
  const meetingName = session.name || "Session";
  const dateTime = start ? dateTimeFormatter.format(start) : "";
  const participants: Participant[] = [];

  return (
    <Box sx={{ minHeight: "100vh", p: 3 }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Action bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 1,
            gap: 2.5,
            px: 3,
            py: 2,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: "text.secondary" }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, flexGrow: 1, textAlign: "center" }}
          >
            {t("coachingNavigator.distributionScreen.title")}
          </Typography>
          <Box sx={{ width: 34 }} />
        </Box>

        <MeetingInfoCard
          meetingName={meetingName}
          dateTime={dateTime}
          participants={participants}
        />
        <EmailTemplateSelector
          selectedTemplate={selectedTemplate}
          templates={templates}
          onSelectTemplate={setSelectedTemplate}
          onCreateTemplate={() => setCreateTemplateOpen(true)}
          creatingTemplate={creatingTemplate}
        />
        <DurationAdjuster
          scheduledDuration={scheduledDuration}
          actualDuration={actualDuration}
          onDurationChange={setActualDuration}
        />

        {/* Footer actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleConfirm}
            disabled={confirming}
          >
            {t("coachingNavigator.distributionScreen.confirmAndFreeze")}
          </Button>
        </Box>
      </Box>

      <CloseConfirmDialog
        open={showCloseDialog}
        onOpenChange={setShowCloseDialog}
        onConfirm={handleCloseConfirm}
      />
      <EmailTemplateDialog
        open={createTemplateOpen}
        onClose={() => setCreateTemplateOpen(false)}
        onSubmit={handleCreateTemplate}
      />
    </Box>
  );
}

export default DistributionScreen;
